import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  ViewChild,
  inject,
} from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { NbWindowService } from '@nebular/theme';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FieldTypeConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material/form-field';
import { Observable, filter, startWith, switchMap, tap } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export interface AutoCompleteOption {
  label: string;
  value: string | number;
}

export interface FormlyAutocompleteConfig {
  filter?: (term: string) => Observable<AutoCompleteOption>;
  format?: (term: string) => Observable<AutoCompleteOption>;
}

@UntilDestroy()
@Component({
  selector: 'vmspdb-component-formly-formly-autocomplete',
  templateUrl: './formly-autocomplete.component.html',
  styleUrls: ['./formly-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FormlyAutocompleteComponent
  extends FieldType<FieldTypeConfig>
  implements AfterViewInit
{
  protected win = inject(NbWindowService);

  filter!: Observable<any>;
  listener = new EventEmitter<string>();

  @ViewChild('filterField') filterField!: ElementRef<HTMLInputElement>;
  displayedLabel = '';

  addModal() {
    this.win
      .open(this.props['modal'], {
        title: this.props['addTitle'] ?? 'Add',
        windowClass: this.props['windowClass'] ?? 'modal-medium',
      })
      .onClose.pipe(
        untilDestroyed(this),
        filter((value) => value),
        tap((uuid) => {
          this.formControl.setValue(uuid);
        }),
      )
      .subscribe();
  }

  displayFn(item: AutoCompleteOption | string): string {
    return typeof item !== 'string' ? (item?.label ?? '') : item;
  }

  clear() {
    this.displayedLabel = '';
    this.filterField.nativeElement.focus();
  }

  ngAfterViewInit() {
    this.filter = this.listener.pipe(
      startWith(this.value),
      debounceTime(250),
      switchMap((term) => this.props['filter'](term)),
    );

    this.formControl.valueChanges
      .pipe(
        startWith(this.value),
        switchMap(
          (term) =>
            this.props['format'](term) as Observable<AutoCompleteOption>,
        ),
        tap((term: AutoCompleteOption) => {
          this.displayedLabel = term.label.toString();
        }),
      )
      .subscribe();

    setTimeout(() => {
      this.options?.detectChanges?.(this.field);
    }, 100);
  }

  doFilter(term: string) {
    if (term === '') {
      this.formControl.setValue(term);
      return;
    }

    // console.log('doFilter', { term });
    this.listener.next(term);
  }

  doBlur() {
    if (this.displayedLabel == '') {
      this.doFilter('');
    }
  }

  optionSelected($event: MatAutocompleteSelectedEvent) {
    const { option } = $event;
    this.formControl.setValue(option.value.value);
    this.props['change']?.(this.field);
  }
}
